/** @jsxImportSource react */
import { useState } from 'react'
import {
  Autocomplete, Box, Button, Checkbox, FormControlLabel, IconButton, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { REMINDERS } from '../tasks/taskModel'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { useEntityTypes, usePublishedForm } from '../../hooks/useCrmQueries'
import { allFields } from '../records/fieldValues'
import { assignable, useMembers, usePersonLabel } from '../records/PersonInput'
import { entityLabel } from '../../utils/helpers'
import ValueEditor from './ValueEditor'
import InsertFieldMenu from './InsertFieldMenu'
import { ACTIONS } from './automationModel'

/** Rows of "field → value" for UPDATE_RECORD / CREATE_RECORD. */
const FieldValues = ({ fields, values, onChange, ctx, addLabel = 'Set a field' }) => {
  const [anchor, setAnchor] = useState(null)
  const byKey = Object.fromEntries(fields.map((f) => [f.fieldKey, f]))
  // roll-ups are calculated from linked records; rules can't set them
  const unused = fields.filter((f) => !(f.fieldKey in values) && f.fieldType !== 'ROLLUP')
  const set = (key, value) => onChange({ ...values, [key]: value })
  const remove = (key) => {
    const next = { ...values }
    delete next[key]
    onChange(next)
  }
  return (
    <Stack spacing={1.5}>
      {Object.entries(values).map(([key, value]) => (
        <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="body2" sx={{ width: 140, flexShrink: 0, pt: 1.25, fontWeight: 500 }} noWrap title={byKey[key]?.label || key}>
            {byKey[key]?.label || <Box component="span" sx={{ color: 'error.main' }}>{key} (missing)</Box>}
          </Typography>
          {byKey[key]
            ? <ValueEditor field={byKey[key]} value={value} onChange={(v) => set(key, v)} ctx={ctx} label={byKey[key].label} allowTemplates />
            : <Box sx={{ flex: 1 }} />}
          <IconButton onClick={() => remove(key)} aria-label={`Remove ${byKey[key]?.label || key}`} sx={{ mt: 0.5 }}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      ))}
      <Box>
        <Button size="small" startIcon={<AddIcon />} disabled={!unused.length} onClick={(e) => setAnchor(e.currentTarget)}>{addLabel}</Button>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { maxHeight: 360 } }}>
          {unused.map((f) => (
            <MenuItem key={f.fieldKey} onClick={() => { setAnchor(null); set(f.fieldKey, null) }}>
              {f.label}{f.readOnly && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>read-only</Typography>}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Stack>
  )
}

const CreateRecordParams = ({ params, onChange, ctx }) => {
  const { forms } = useEntityTypes()
  const { data: form } = usePublishedForm(params.formCode || null)
  const fields = form ? allFields(form) : []
  return (
    <Stack spacing={2}>
      <TextField select label="Record to create" value={params.formCode || ''} sx={{ width: { xs: '100%', sm: 360 }, flexShrink: 0 }}
        onChange={(e) => onChange({ formCode: e.target.value, values: {} })}>
        {forms.map((f) => <MenuItem key={f.code} value={f.code}>{entityLabel(f.entityType)} · {f.name}</MenuItem>)}
      </TextField>
      {form && (
        <>
          <Typography variant="caption" color="text.secondary">
            Fields you leave out get their form default. {fields.some((f) => f.fieldType === 'LOOKUP' && f.lookupEntityType === ctx.entityType)
              && 'Set a lookup to “The triggering record” to link them.'}
          </Typography>
          <FieldValues fields={fields} values={params.values || {}} onChange={(values) => onChange({ ...params, values })} ctx={ctx} addLabel="Fill a field" />
        </>
      )}
    </Stack>
  )
}

const NotifyParams = ({ params, onChange, ctx }) => {
  const { data: members = [] } = useMembers()
  const personLabel = usePersonLabel()
  const personFields = ctx.sourceFields.filter((f) => f.fieldType === 'USER')
  // a time-based run has no "person who made the change"
  const options = [...(ctx.timeBased ? [] : ['$actor']), ...personFields.map((f) => `field:${f.fieldKey}`), ...assignable(members).map((m) => m.userId)]
  const labelOf = (v) => {
    if (v === '$actor') return 'Person who made the change'
    if (v.startsWith('field:')) return `The record’s ${ctx.sourceByKey[v.slice(6)]?.label || v.slice(6)}`
    return personLabel(v)
  }
  const set = (patch) => onChange({ ...params, ...patch })
  return (
    <Stack spacing={2}>
      <Autocomplete multiple options={options} value={params.recipients || []} getOptionLabel={labelOf}
        onChange={(_, recipients) => set({ recipients })} filterSelectedOptions
        groupBy={(v) => (v === '$actor' || v.startsWith('field:') ? 'From the record' : 'Members')}
        renderInput={(p) => <TextField {...p} label="Notify" placeholder={params.recipients?.length ? '' : 'Owner, a member…'} />} />
      <Box>
        <TextField label="Title" fullWidth value={params.title || ''} onChange={(e) => set({ title: e.target.value })} inputProps={{ maxLength: 200 }}
          placeholder="{{record.title}} was won" />
        <InsertFieldMenu fields={ctx.sourceFields} timeBased={ctx.timeBased} onInsert={(p) => set({ title: `${params.title || ''}${p}` })} />
      </Box>
      <Box>
        <TextField label="Message (optional)" fullWidth multiline minRows={2} value={params.message || ''} onChange={(e) => set({ message: e.target.value })}
          inputProps={{ maxLength: 1000 }} />
        <InsertFieldMenu fields={ctx.sourceFields} timeBased={ctx.timeBased} onInsert={(p) => set({ message: `${params.message || ''}${p}` })} />
      </Box>
    </Stack>
  )
}

/** Who: "Person who made the change", a person field of the record, or a member. */
const PersonSpecInput = ({ label, value, onChange, ctx }) => {
  const { data: members = [] } = useMembers()
  const personLabel = usePersonLabel()
  const personFields = ctx.sourceFields.filter((f) => f.fieldType === 'USER')
  const options = [...(ctx.timeBased ? [] : ['$actor']), ...personFields.map((f) => `field:${f.fieldKey}`), ...assignable(members).map((m) => m.userId)]
  const labelOf = (v) => {
    if (!v) return ''
    if (v === '$actor') return 'Person who made the change'
    if (v.startsWith('field:')) return `The record’s ${ctx.sourceByKey[v.slice(6)]?.label || v.slice(6)}`
    return personLabel(v)
  }
  return (
    <Autocomplete options={options} value={value || null} getOptionLabel={labelOf} onChange={(_, v) => onChange(v || '')}
      groupBy={(v) => (v === '$actor' || v.startsWith('field:') ? 'From the record' : 'Members')}
      renderInput={(p) => <TextField {...p} label={label} placeholder="Owner, a member…" />} sx={{ width: { xs: '100%', sm: 360 } }} />
  )
}

const CreateTaskParams = ({ params, onChange, ctx }) => {
  const set = (patch) => onChange({ ...params, ...patch })
  const hasDue = params.dueInDays != null
  return (
    <Stack spacing={2}>
      <Box>
        <TextField label="Task" fullWidth value={params.title || ''} onChange={(e) => set({ title: e.target.value })} inputProps={{ maxLength: 200 }}
          placeholder="Call {{record.title}} about the renewal" />
        <InsertFieldMenu fields={ctx.sourceFields} timeBased={ctx.timeBased} onInsert={(p) => set({ title: `${params.title || ''}${p}` })} />
      </Box>
      <PersonSpecInput label="For" value={params.assignee} onChange={(assignee) => set({ assignee })} ctx={ctx} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField select label="Due" value={hasDue ? 'yes' : 'no'} sx={{ width: { xs: '100%', sm: 160 } }}
          onChange={(e) => set(e.target.value === 'yes' ? { dueInDays: 1, dueTime: '10:00' } : { dueInDays: null, dueTime: null, remindMinutesBefore: null })}>
          <MenuItem value="yes">In some days</MenuItem>
          <MenuItem value="no">No due date</MenuItem>
        </TextField>
        {hasDue && (
          <>
            <TextField label="Days from now" type="number" value={params.dueInDays} sx={{ width: { xs: '100%', sm: 150 } }}
              inputProps={{ min: 0, max: 365 }} helperText="0 = today"
              onChange={(e) => set({ dueInDays: Math.min(365, Math.max(0, Math.trunc(Number(e.target.value) || 0))) })} />
            <TextField label="At" type="time" value={params.dueTime || '10:00'} sx={{ width: { xs: '100%', sm: 130 } }} InputLabelProps={{ shrink: true }}
              onChange={(e) => set({ dueTime: e.target.value })} />
            <TextField select label="Reminder" value={params.remindMinutesBefore ?? ''} sx={{ width: { xs: '100%', sm: 200 } }}
              SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
              onChange={(e) => set({ remindMinutesBefore: e.target.value === '' ? null : Number(e.target.value) })}>
              {REMINDERS.map((r) => <MenuItem key={String(r.value)} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
          </>
        )}
      </Stack>
      <Box>
        <TextField label="Notes (optional)" fullWidth multiline minRows={2} value={params.notes || ''} onChange={(e) => set({ notes: e.target.value })} inputProps={{ maxLength: 5000 }} />
        <InsertFieldMenu fields={ctx.sourceFields} timeBased={ctx.timeBased} onInsert={(p) => set({ notes: `${params.notes || ''}${p}` })} />
      </Box>
    </Stack>
  )
}

const SendEmailParams = ({ params, onChange, ctx }) => {
  const set = (patch) => onChange({ ...params, ...patch })
  const addressFields = ctx.sourceFields.filter((f) => f.fieldType === 'EMAIL' || f.fieldType === 'USER')
  const options = addressFields.map((f) => `field:${f.fieldKey}`)
  const labelOf = (v) => (v.startsWith('field:') ? `The record’s ${ctx.sourceByKey[v.slice(6)]?.label || v.slice(6)}` : v)
  return (
    <Stack spacing={2}>
      <Autocomplete multiple freeSolo options={options} value={params.to || []} getOptionLabel={labelOf} filterSelectedOptions
        onChange={(_, to) => set({ to: to.map((t) => t.trim()).filter(Boolean) })}
        renderInput={(p) => <TextField {...p} label="To" placeholder={params.to?.length ? '' : 'The record’s Email, or type an address'}
          helperText="Person fields send to that member’s email" />} />
      <Box>
        <TextField label="Subject" fullWidth value={params.subject || ''} onChange={(e) => set({ subject: e.target.value })} inputProps={{ maxLength: 300 }}
          placeholder="Your quote for {{record.title}}" />
        <InsertFieldMenu fields={ctx.sourceFields} timeBased={ctx.timeBased} onInsert={(p) => set({ subject: `${params.subject || ''}${p}` })} />
      </Box>
      <Box>
        <TextField label="Message" fullWidth multiline minRows={5} value={params.body || ''} onChange={(e) => set({ body: e.target.value })} inputProps={{ maxLength: 20000 }} />
        <InsertFieldMenu fields={ctx.sourceFields} timeBased={ctx.timeBased} onInsert={(p) => set({ body: `${params.body || ''}${p}` })} />
      </Box>
      <Typography variant="caption" color="text.secondary">Sent from the CRM’s address in the workspace’s name, and logged on the record’s timeline.</Typography>
    </Stack>
  )
}

const RoundRobinParams = ({ params, onChange, ctx }) => {
  const { data: members = [] } = useMembers()
  const personLabel = usePersonLabel()
  const set = (patch) => onChange({ ...params, ...patch })
  const personFields = ctx.sourceFields.filter((f) => f.fieldType === 'USER')
  return (
    <Stack spacing={2}>
      <TextField select label="Fill" value={params.field || ''} sx={{ width: { xs: '100%', sm: 260 } }} onChange={(e) => set({ field: e.target.value })}>
        {personFields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
      </TextField>
      <Autocomplete multiple options={assignable(members).map((m) => m.userId)} value={params.members || []} getOptionLabel={personLabel}
        onChange={(_, list) => set({ members: list })} filterSelectedOptions
        renderInput={(p) => <TextField {...p} label="Taking turns" placeholder={params.members?.length ? '' : 'Choose members'} />} />
      <FormControlLabel control={<Checkbox checked={params.onlyIfEmpty !== false} onChange={(e) => set({ onlyIfEmpty: e.target.checked })} />}
        label="Only when it’s empty (don’t reassign records that already have someone)" />
      <Typography variant="caption" color="text.secondary">Members who leave or are suspended are skipped.</Typography>
    </Stack>
  )
}

/** One numbered step of the "Then" list. */
const ActionEditor = ({ action, index, count, onChange, onRemove, onMove, ctx }) => {
  const meta = ACTIONS.find((a) => a.type === action.type)
  const setParams = (params) => onChange({ ...action, params })
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {index + 1}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">{meta?.label || action.type}</Typography>
          <Typography variant="caption" color="text.secondary">{meta?.hint}</Typography>
        </Box>
        <Tooltip title="Move up"><span><IconButton size="small" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move step up"><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Move down"><span><IconButton size="small" disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Move step down"><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Remove step"><IconButton size="small" onClick={onRemove} aria-label="Remove step"><CloseIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
      {action.type === 'UPDATE_RECORD' && (
        <FieldValues fields={ctx.sourceFields} values={action.params?.values || {}} onChange={(values) => setParams({ values })} ctx={ctx} />
      )}
      {action.type === 'CREATE_RECORD' && <CreateRecordParams params={action.params || {}} onChange={setParams} ctx={ctx} />}
      {action.type === 'NOTIFY' && <NotifyParams params={action.params || {}} onChange={setParams} ctx={ctx} />}
      {action.type === 'CREATE_TASK' && <CreateTaskParams params={action.params || {}} onChange={setParams} ctx={ctx} />}
      {action.type === 'SEND_EMAIL' && <SendEmailParams params={action.params || {}} onChange={setParams} ctx={ctx} />}
      {action.type === 'ASSIGN_ROUND_ROBIN' && <RoundRobinParams params={action.params || {}} onChange={setParams} ctx={ctx} />}
    </Paper>
  )
}

export default ActionEditor
